import React from 'react';
import GeoChoropleth from '../../components/geo-choropleth';
import MI from '../../endpoints/market-intelligence';
import * as MiStats from '../../endpoints/mi-stats';
import PostalBubbleMap, { isMetroScale } from './postal-bubble-map';

// MI Phase 2 — Geographic panel (MI_GEO_MAPS.md; mockup mi-geo-maps.html)
// + the §58 POSTAL CODE view (UX note 2026-08-17).
//
// Two views, toggled in the header and remembered per market:
//  - Region — the shipped Territory choropleth over /dashboard/geo
//    regions, with the postal drill table beneath (unchanged).
//  - Postal codes — a bounding-box bubble map from
//    /v1/location/stats $groupBy=postal_code scoped to the market
//    (rows carry lat/lng/city/regionCode) so metro markets fill the
//    frame instead of drowning in the lower 48. One bubble per zip,
//    radius ∝ √metric; hovering a table row highlights its bubble and
//    vice versa. Rows without lat/lng stay in the table with the
//    "Map shows visits with location data." footnote.
// Default: metro-scale rows (single region or <~500 km span) open on
// Postal codes; national spread opens on Region (§58 default logic).

const METRIC_TABS = [['views', 'Views'], ['sessions', 'Sessions'], ['conversions', 'Conversions']];
const CHORO_LABELS = {
  views: 'Views',
  sessions: 'Sessions',
  conversions: 'Conversions'
};
const f = (r, k) => Number(r[k] ?? r[k.charAt(0).toLowerCase() + k.slice(1)]) || 0;
const s = (r, k) => r[k] ?? r[k.charAt(0).toLowerCase() + k.slice(1)];
const unwrap = resp => resp && resp.items || (Array.isArray(resp) ? resp : []);
const viewKey = market => `plunk-mi-geoview-${market}`;
export default class GeoMaps extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      data: null,
      zips: null,
      failed: false,
      metric: 'views',
      regionFilter: null,
      view: null,
      hovered: null,
      basemap: 'us',
      countryFilter: null
    };
    this._alive = true;
  }
  componentDidMount() {
    this._alive = true;
    this.load();
  }
  componentWillUnmount() {
    this._alive = false;
  }
  componentDidUpdate(prev) {
    if (prev.market !== this.props.market || prev.range !== this.props.range) {
      this.setState({
        data: null,
        zips: null,
        failed: false,
        regionFilter: null,
        view: null,
        hovered: null
      });
      this.load();
    }
  }
  load() {
    MI.getGeo({
      location: this.props.market,
      range: this.props.range,
      level: 'both'
    }).then(d => {
      if (this._alive) this.setState({
        data: d || {}
      });
    }).catch(() => {
      if (this._alive) this.setState({
        failed: true
      });
    });
    // §58 postal rows with lat/lng — the location-stats rail, market-scoped.
    try {
      const win = MiStats.rangeToWindow(this.props.range);
      if (!win) throw new Error('range has no explicit window'); // all_time / unknown → no postal rail
      const {
        start,
        end
      } = win;
      MiStats.getZipsByPlace({
        place: this.props.market,
        start,
        end
      }).then(resp => {
        if (this._alive) this.setState({
          zips: unwrap(resp)
        }, () => this.resolveView());
      }).catch(() => {
        if (this._alive) this.setState({
          zips: []
        }, () => this.resolveView());
      });
    } catch (e) {
      this.setState({
        zips: []
      });
    }
  }
  resolveView() {
    if (this.state.view) return;
    let stored = null;
    try {
      stored = localStorage.getItem(viewKey(this.props.market));
    } catch (e) {/* private mode */}
    const view = stored === 'postal' || stored === 'region' ? stored : this.state.zips && this.state.zips.length && isMetroScale(this.state.zips) ? 'postal' : 'region';
    this.setState({
      view
    });
  }
  setView(view) {
    this.setState({
      view
    });
    try {
      localStorage.setItem(viewKey(this.props.market), view);
    } catch (e) {/* private mode */}
  }
  metricKey() {
    return this.state.metric === 'views' ? 'Views' : this.state.metric === 'sessions' ? 'Sessions' : 'Conversions';
  }
  choroRows(regions) {
    const key = this.metricKey();
    return (regions || []).filter(r => s(r, 'RegionCode')).map(r => ({
      code: String(s(r, 'RegionCode')).toUpperCase(),
      revenue: f(r, key),
      orders: 0
    }));
  }

  // World basemap: roll regions up to ISO-2 country (rows carry CountryCode;
  // US-only data just shades US on the world map).
  countryRows(regions) {
    const key = this.metricKey();
    const by = {};
    (regions || []).forEach(r => {
      const cc = String(s(r, 'CountryCode') || '').toUpperCase();
      if (cc.length !== 2) return;
      by[cc] = (by[cc] || 0) + f(r, key);
    });
    return Object.keys(by).map(cc => ({
      code: cc,
      revenue: by[cc],
      orders: 0
    }));
  }

  // Does the geo data span more than the US? (drives the World toggle's worth)
  hasNonUsGeo(regions) {
    return (regions || []).some(r => {
      const cc = String(s(r, 'CountryCode') || '').toUpperCase();
      return cc && cc !== 'US';
    });
  }
  render() {
    const {
      data,
      zips,
      failed,
      metric,
      regionFilter,
      view,
      hovered
    } = this.state;
    if (failed) return null;
    if (!data || view == null) {
      return /*#__PURE__*/React.createElement("section", {
        className: "mi-panel mi-panel-full",
        "aria-label": "Geographic"
      }, /*#__PURE__*/React.createElement("header", {
        className: "mi-panel-header"
      }, /*#__PURE__*/React.createElement("h2", null, "Geographic")), /*#__PURE__*/React.createElement("div", {
        className: "mi-panel-body"
      }, /*#__PURE__*/React.createElement("div", {
        className: "mi-panel-loading"
      }, "Loading geography\u2026")));
    }
    const regions = data.regions || data.Regions || [];
    const dashPostal = data.postalCodes || data.PostalCodes || [];
    const hasZips = (zips || []).length > 0;
    if (!regions.length && !dashPostal.length && !hasZips) return null;
    const num = v => (Number(v) || 0).toLocaleString();
    const rightCell = {
      textAlign: 'right'
    };
    const postalMode = view === 'postal' && hasZips;

    // ── postal view data (location-stats rows: camelCase + lat/lng) ──
    const metricField = metric === 'views' ? 'views' : metric === 'sessions' ? 'sessions' : 'conversions';
    const zipSorted = [...(zips || [])].sort((a, b) => (Number(b[metricField]) || 0) - (Number(a[metricField]) || 0));
    const mapped = zipSorted.filter(r => Number.isFinite(Number(r.lat)) && Number.isFinite(Number(r.lng)));
    const unmapped = zipSorted.length - mapped.length;
    const bubbleRows = mapped.map(r => ({
      lat: r.lat,
      lng: r.lng,
      regionCode: r.regionCode,
      label: r.postalCode || '—',
      sub: r.city ? String(r.city).replace(/\b\w/g, c => c.toUpperCase()) : '',
      value: Number(r[metricField]) || 0
    }));
    const zipShown = zipSorted.slice(0, 15);

    // ── region view data (unchanged /dashboard/geo path) ──
    const {
      basemap,
      countryFilter
    } = this.state;
    const worldMap = basemap === 'world';
    const drillZips = worldMap ? countryFilter ? dashPostal.filter(r => String(s(r, 'CountryCode') || '').toUpperCase() === countryFilter) : dashPostal : regionFilter ? dashPostal.filter(r => String(s(r, 'RegionCode') || '').toUpperCase() === regionFilter) : dashPostal;
    const drillSorted = [...drillZips].sort((a, b) => f(b, 'Views') - f(a, 'Views'));
    const drillShown = drillSorted.slice(0, 15);
    return /*#__PURE__*/React.createElement("section", {
      className: "mi-panel mi-panel-full",
      "aria-label": "Geographic"
    }, /*#__PURE__*/React.createElement("header", {
      className: "mi-panel-header"
    }, /*#__PURE__*/React.createElement("h2", null, "Geographic"), /*#__PURE__*/React.createElement("div", {
      className: "mi-toggle-group",
      style: {
        marginRight: 12
      }
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: `mi-sort-tab ${!postalMode ? 'active' : ''}`,
      onClick: () => this.setView('region')
    }, "Region"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: `mi-sort-tab ${postalMode ? 'active' : ''}`,
      disabled: !hasZips,
      title: hasZips ? undefined : 'No mapped postal rows in this range',
      onClick: () => this.setView('postal')
    }, "Postal codes")), /*#__PURE__*/React.createElement("div", {
      className: "mi-toggle-group"
    }, METRIC_TABS.map(([key, label]) => /*#__PURE__*/React.createElement("button", {
      key: key,
      type: "button",
      className: `mi-sort-tab ${metric === key ? 'active' : ''}`,
      onClick: () => this.setState({
        metric: key
      })
    }, label)))), /*#__PURE__*/React.createElement("div", {
      className: "mi-panel-body"
    }, postalMode ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PostalBubbleMap, {
      rows: bubbleRows,
      hovered: hovered,
      onHover: l => this.setState({
        hovered: l
      }),
      counties: true
    }), unmapped > 0 && /*#__PURE__*/React.createElement("div", {
      className: "mi-report-meta",
      style: {
        marginTop: 6
      }
    }, "Map shows visits with location data."), !this.props.hideTable && /*#__PURE__*/React.createElement("table", {
      className: "mi-report-table",
      style: {
        marginTop: '8px'
      }
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Postal Code"), /*#__PURE__*/React.createElement("th", null, "City"), /*#__PURE__*/React.createElement("th", null, "Region"), /*#__PURE__*/React.createElement("th", {
      style: rightCell
    }, "Views"), /*#__PURE__*/React.createElement("th", {
      style: rightCell
    }, "Sessions"), /*#__PURE__*/React.createElement("th", {
      style: rightCell
    }, "Conversions"), /*#__PURE__*/React.createElement("th", {
      style: rightCell
    }, "Conv. rate"), /*#__PURE__*/React.createElement("th", {
      style: rightCell
    }, "Unique users"))), /*#__PURE__*/React.createElement("tbody", null, zipShown.map((r, i) => {
      const sess = Number(r.sessions) || 0;
      const conv = Number(r.conversions) || 0;
      const hot = hovered === (r.postalCode || '—');
      return /*#__PURE__*/React.createElement("tr", {
        key: i,
        onMouseEnter: () => this.setState({
          hovered: r.postalCode || '—'
        }),
        onMouseLeave: () => this.setState({
          hovered: null
        }),
        style: hot ? {
          background: 'var(--plk-surface-alt, #f3f3f3)'
        } : undefined
      }, /*#__PURE__*/React.createElement("td", null, r.postalCode || '—'), /*#__PURE__*/React.createElement("td", {
        style: {
          textTransform: 'capitalize'
        }
      }, String(r.city || '—').toLowerCase()), /*#__PURE__*/React.createElement("td", null, String(r.regionCode || '—').toUpperCase()), /*#__PURE__*/React.createElement("td", {
        style: rightCell
      }, num(r.views)), /*#__PURE__*/React.createElement("td", {
        style: rightCell
      }, num(sess)), /*#__PURE__*/React.createElement("td", {
        style: rightCell
      }, num(conv)), /*#__PURE__*/React.createElement("td", {
        style: rightCell
      }, sess > 0 ? `${(conv / sess * 100).toFixed(1)}%` : '—'), /*#__PURE__*/React.createElement("td", {
        style: rightCell
      }, num(r.uniqueUsers)));
    }))), !this.props.hideTable && zipSorted.length > 15 && /*#__PURE__*/React.createElement("div", {
      className: "mi-report-meta",
      style: {
        marginTop: '6px'
      }
    }, "Top 15 of ", num(zipSorted.length), " zip codes shown.")) : /*#__PURE__*/React.createElement(React.Fragment, null, regions.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "mi-toggle-group",
      style: {
        marginBottom: 8
      }
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: `mi-sort-tab ${!worldMap ? 'active' : ''}`,
      onClick: () => this.setState({
        basemap: 'us'
      })
    }, "United States"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: `mi-sort-tab ${worldMap ? 'active' : ''}`,
      onClick: () => this.setState({
        basemap: 'world'
      }),
      title: this.hasNonUsGeo(regions) ? undefined : 'This range is US-only; the world map shades the US'
    }, "World")), /*#__PURE__*/React.createElement(GeoChoropleth, {
      scope: worldMap ? 'world' : 'us',
      rows: worldMap ? this.countryRows(regions) : this.choroRows(regions),
      metric: "revenue",
      labels: {
        revenue: CHORO_LABELS[metric]
      },
      height: "340px",
      onSelect: code => {
        const up = String(code).toUpperCase();
        if (worldMap) this.setState({
          countryFilter: countryFilter === up ? null : up
        });else this.setState({
          regionFilter: regionFilter === up ? null : up
        });
      }
    })), worldMap && countryFilter && /*#__PURE__*/React.createElement("div", {
      className: "mi-report-meta",
      style: {
        margin: '6px 0'
      }
    }, "Zip codes in ", /*#__PURE__*/React.createElement("strong", null, countryFilter), " \u2014", ' ', /*#__PURE__*/React.createElement("a", {
      style: {
        cursor: 'pointer'
      },
      onClick: () => this.setState({
        countryFilter: null
      })
    }, "show all")), !worldMap && regionFilter && /*#__PURE__*/React.createElement("div", {
      className: "mi-report-meta",
      style: {
        margin: '6px 0'
      }
    }, "Zip codes in ", /*#__PURE__*/React.createElement("strong", null, regionFilter), " \u2014", ' ', /*#__PURE__*/React.createElement("a", {
      style: {
        cursor: 'pointer'
      },
      onClick: () => this.setState({
        regionFilter: null
      })
    }, "show all")), drillShown.length > 0 ? /*#__PURE__*/React.createElement("table", {
      className: "mi-report-table",
      style: {
        marginTop: '8px'
      }
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Postal Code"), /*#__PURE__*/React.createElement("th", null, "City"), /*#__PURE__*/React.createElement("th", null, "Region"), /*#__PURE__*/React.createElement("th", {
      style: rightCell
    }, "Views"), /*#__PURE__*/React.createElement("th", {
      style: rightCell
    }, "Sessions"), /*#__PURE__*/React.createElement("th", {
      style: rightCell
    }, "Conversions"), /*#__PURE__*/React.createElement("th", {
      style: rightCell
    }, "Conv. rate"), /*#__PURE__*/React.createElement("th", {
      style: rightCell
    }, "Unique users"))), /*#__PURE__*/React.createElement("tbody", null, drillShown.map((r, i) => {
      const sess = f(r, 'Sessions');
      const conv = f(r, 'Conversions');
      return /*#__PURE__*/React.createElement("tr", {
        key: i
      }, /*#__PURE__*/React.createElement("td", null, s(r, 'PostalCode')), /*#__PURE__*/React.createElement("td", {
        style: {
          textTransform: 'capitalize'
        }
      }, String(s(r, 'City') || '—').toLowerCase()), /*#__PURE__*/React.createElement("td", null, String(s(r, 'RegionCode') || '—').toUpperCase()), /*#__PURE__*/React.createElement("td", {
        style: rightCell
      }, num(f(r, 'Views'))), /*#__PURE__*/React.createElement("td", {
        style: rightCell
      }, num(sess)), /*#__PURE__*/React.createElement("td", {
        style: rightCell
      }, num(conv)), /*#__PURE__*/React.createElement("td", {
        style: rightCell
      }, sess > 0 ? `${(conv / sess * 100).toFixed(1)}%` : '—'), /*#__PURE__*/React.createElement("td", {
        style: rightCell
      }, num(f(r, 'UniqueUsers'))));
    }))) : (regionFilter || countryFilter) && /*#__PURE__*/React.createElement("div", {
      className: "mi-report-meta",
      style: {
        marginTop: '8px'
      }
    }, "No tracked zips in ", countryFilter || regionFilter, " for this range."), drillSorted.length > 15 && /*#__PURE__*/React.createElement("div", {
      className: "mi-report-meta",
      style: {
        marginTop: '6px'
      }
    }, "Top 15 of ", num(drillSorted.length), " zip codes shown", dashPostal.length >= 500 ? ' (source capped at 500 rows)' : '', "."))));
  }
}